/**
 * Lightweight HS256 JWT signing using the Web Crypto API (no dependencies).
 * Use for signing only; verification is typically done server-side.
 */

const base64UrlEncode = (input: ArrayBuffer | Uint8Array): string => {
	const bytes = input instanceof ArrayBuffer ? new Uint8Array(input) : input;
	let binary = "";
	for (let i = 0; i < bytes.length; i++) {
		binary += String.fromCharCode(bytes[i]);
	}
	return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const getKey = async (secret: string): Promise<CryptoKey> =>
	crypto.subtle.importKey("raw", new TextEncoder().encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);

/**
 * Signs a JWT with HS256. Header and payload are JSON-serialized and base64url-encoded.
 */
export const signHs256Jwt = async (header: object, payload: object, secret: string): Promise<string> => {
	const encodedHeader = base64UrlEncode(new TextEncoder().encode(JSON.stringify(header)));
	const encodedPayload = base64UrlEncode(new TextEncoder().encode(JSON.stringify(payload)));
	const message = `${encodedHeader}.${encodedPayload}`;
	const key = await getKey(secret);
	const signature = await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(message));
	return `${message}.${base64UrlEncode(signature)}`;
};
